// creditConversionRates — Token/byte/second → credits conversion.
//
// Defaults är baseline; produktion bör inject:a per-org overrides.

import type { CreditKind } from '@/lib/creditTypes';

export interface ConversionRates {
  /** Tokens per 1 credit, per-model. */
  tokensPerCreditByModel: Record<string, number>;
  /** Default för okända models. */
  defaultTokensPerCredit: number;
  /** Bytes × dagar per storage-credit. */
  byteDaysPerStorageCredit: number;
  /** Compute-sekunder per credit, per CPU-klass. */
  secondsPerCreditByCpuClass: Record<string, number>;
  defaultSecondsPerCredit: number;
  /** USD per credit (för reverse-conversion i billing). */
  usdPerCredit: number;
  /** Connector-calls per credit. */
  callsPerCredit: number;
  /** Webhook-deliveries per credit. */
  deliveriesPerCredit: number;
  /** Export-operations per credit. */
  exportsPerCredit: number;
  /** MB egress per credit. */
  mbPerBandwidthCredit: number;
}

export const DEFAULT_RATES: ConversionRates = {
  tokensPerCreditByModel: {
    'gpt-4o': 1_000,
    'gpt-4o-mini': 5_000,
    'claude-opus-4-7': 800,
    'claude-sonnet-4-6': 2_000,
    'claude-haiku-4-5': 10_000,
  },
  defaultTokensPerCredit: 2_000,
  byteDaysPerStorageCredit: 10 * 1024 * 1024 * 1024, // 10 GB × 1 day = 1 credit
  secondsPerCreditByCpuClass: {
    small: 10,
    medium: 5,
    large: 2,
  },
  defaultSecondsPerCredit: 5,
  usdPerCredit: 0.01,
  callsPerCredit: 10,
  deliveriesPerCredit: 10,
  exportsPerCredit: 1,
  mbPerBandwidthCredit: 100,
};

export function tokensToCredits(
  tokens: number,
  model: string,
  rates: ConversionRates = DEFAULT_RATES,
): number {
  if (tokens <= 0) return 0;
  const tokensPerCredit = rates.tokensPerCreditByModel[model] ?? rates.defaultTokensPerCredit;
  return tokens / tokensPerCredit;
}

export function bytesToStorageCredits(
  bytes: number,
  days: number,
  rates: ConversionRates = DEFAULT_RATES,
): number {
  if (bytes <= 0 || days <= 0) return 0;
  return (bytes * days) / rates.byteDaysPerStorageCredit;
}

export function secondsToComputeCredits(
  seconds: number,
  cpuClass: string = 'medium',
  rates: ConversionRates = DEFAULT_RATES,
): number {
  if (seconds <= 0) return 0;
  const secondsPerCredit = rates.secondsPerCreditByCpuClass[cpuClass] ?? rates.defaultSecondsPerCredit;
  return seconds / secondsPerCredit;
}

export function usdToCredits(
  usd: number,
  rates: ConversionRates = DEFAULT_RATES,
): number {
  if (usd <= 0) return 0;
  return usd / rates.usdPerCredit;
}

export function callsToConnectorCredits(
  calls: number,
  rates: ConversionRates = DEFAULT_RATES,
): number {
  if (calls <= 0) return 0;
  return calls / rates.callsPerCredit;
}

export function deliveriesToWebhookCredits(
  deliveries: number,
  rates: ConversionRates = DEFAULT_RATES,
): number {
  if (deliveries <= 0) return 0;
  return deliveries / rates.deliveriesPerCredit;
}

export function exportsToCredits(
  exports: number,
  rates: ConversionRates = DEFAULT_RATES,
): number {
  if (exports <= 0) return 0;
  return exports / rates.exportsPerCredit;
}

export function mbToBandwidthCredits(
  mb: number,
  rates: ConversionRates = DEFAULT_RATES,
): number {
  if (mb <= 0) return 0;
  return mb / rates.mbPerBandwidthCredit;
}

export function convertBetweenKinds(
  fromKind: CreditKind,
  toKind: CreditKind,
  amount: number,
  rates: ConversionRates = DEFAULT_RATES,
): number | null {
  if (fromKind === toKind) return amount;
  // Gå via USD som intermediär: from → usd → to (med 20 % markup för down-conversion)
  const fromUsd = amount * rates.usdPerCredit;
  if (fromKind === 'ai' && toKind === 'compute') {
    return (fromUsd / rates.usdPerCredit) * 0.8;
  }
  return null;
}

export function mergeRates(
  base: ConversionRates,
  override: Partial<ConversionRates>,
): ConversionRates {
  return {
    ...base,
    ...override,
    tokensPerCreditByModel: {
      ...base.tokensPerCreditByModel,
      ...(override.tokensPerCreditByModel ?? {}),
    },
    secondsPerCreditByCpuClass: {
      ...base.secondsPerCreditByCpuClass,
      ...(override.secondsPerCreditByCpuClass ?? {}),
    },
  };
}
